import React from "react";
import { useQuery, gql } from "@apollo/client";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import ShareParkTable, {
  usePaginationAndFilters,
} from "../../components/table/ShareParkTable";
import {
  GetMessagesQuery,
  GetMessageQuery,
  MessageFilterInput,
} from "../../gql/graphql";
import { ShareparkDateTime } from "../../components/LocalTime";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/authContext";

const GET_MESSAGES = gql`
  query GetMessages(
    $where: MessageFilterInput
    $take: Int
    $skip: Int
    $orderBy: [MessageSortInput!]
  ) {
    messages(where: $where, take: $take, skip: $skip, order: $orderBy) {
      items {
        id
        parkerId
        userId
        site {
          id
          name
        }
        recipient
        messageType
        sentTime
        isSent
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      totalCount
    }
  }
`;

type Message = NonNullable<
  NonNullable<NonNullable<GetMessageQuery["messages"]>["items"]>[number]
>;
const columnHelper = createColumnHelper<Message>();

const baseColumns = [
  columnHelper.accessor("sentTime", {
    header: "Sent",
    cell: (info) => {
      return (
        <Link to={`/messages/${info.row.original?.id}`}>
          <ShareparkDateTime timestamp={info.row.original?.sentTime} />
        </Link>
      );
    },
  }),
  columnHelper.accessor("recipient", {
    header: "Email / Phone",
  }),
];

const siteColumn = columnHelper.accessor((row) => row.site?.name ?? null, {
  id: "siteName",
  header: "Site",
  cell: (info) => {
    const site = info.row.original?.site;
    return site ? (
      <Link to={`/sites/${site.id}`}>{site.name}</Link>
    ) : (
      <span className="text-muted">-</span>
    );
  },
});

export const Messages = () => {
  const { roles } = useAuth();
  const isSuperUser = roles.includes("SuperUser");
  
  const columns = React.useMemo(() => {
    if (isSuperUser) {
      return [siteColumn,...baseColumns];
    }
    return baseColumns;
  }, [isSuperUser]);
  const { pagination, setPagination, columnFilters, setColumnFilters } =
    usePaginationAndFilters();

  // Add sorting state
  const [sorting, setSorting] = React.useState([
    {
      id: "sentTime", // Default sorting column
      desc: true, // Default sorting direction (descending)
    },
  ]);

  // const where = React.useMemo(() => {
  //   const where: MessageFilterInput = {};

  //   for (const filter of columnFilters) {
  //     switch (filter.id) {
  //       case "eventDate":
  //         if (filter.value != null) {
  //           where.eventDate = { eq: String(filter.value) };
  //         }
  //         break;
  //       case "eventType":
  //         if (filter.value != null) {
  //           where.eventType = { eq: String(filter.value) };
  //         }
  //         break;
  //       case "userName":
  //         if (filter.value != null) {
  //           where.userName = { contains: String(filter.value) };
  //         }
  //         break;
  //       case "entityType":
  //         if (filter.value != null) {
  //           where.entityType = { contains: String(filter.value) };
  //         }
  //         break;
  //       case "entityId":
  //         if (filter.value != null) {
  //           where.entityId = { contains: String(filter.value) };
  //         }
  //         break;
  //     }
  //   }

  //   return where;
  // }, [columnFilters]);

  const orderBy = React.useMemo(() => {
    if (sorting.length > 0) {
      const sort = sorting[0];
      return {
        [sort.id]: sort.desc ? "DESC" : "ASC",
      };
    }
    return undefined;
  }, [sorting]);

  const { loading, data } = useQuery<GetMessagesQuery>(GET_MESSAGES, {
    variables: {
      where: {}, // Add your filter logic here if needed
      take: pagination.pageSize,
      skip: pagination.pageSize * pagination.pageIndex,
      orderBy: orderBy ? [orderBy] : undefined,
    },
    fetchPolicy: "network-only",
  });

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Messages</h1>
      </div>

      <ShareParkTable
        data={data?.messages ?? { items: [], totalCount: 0 }}
        columns={columns as ColumnDef<Message>[]}
        pagination={pagination}
        setPagination={setPagination}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        loading={loading}
        sorting={sorting} // Pass sorting state
        setSorting={setSorting} // Pass sorting setter
      />
    </>
  );
};
